/**
 * B1085 — Durées par étape du pipeline (Eric 2026-09-05 SPEC STRICK PROD).
 *
 * « Le traitement semble plus lent depuis dix jours » ne se tranche pas à
 * l'œil : ce tableau compare la durée médiane des derniers jours à celle des
 * jours précédents, étape par étape, et le dit en français.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Timer } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICT = {
  "plus lent": "bg-red-50 text-red-700",
  "plus rapide": "bg-emerald-50 text-emerald-700",
  stable: "bg-zinc-100 text-zinc-600",
  "pas assez de mesures": "bg-zinc-100 text-zinc-500",
};

const secs = (ms) => (ms === null || ms === undefined ? "—" : `${(ms / 1000).toFixed(1)} s`);

export const PipelineDurationsPanel = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/health-monitor/pipeline-durations?days=30&recent_days=10`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;
  const stages = data.stages || [];

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5 mb-6"
      data-testid="pipeline-durations"
    >
      <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
        <Timer size={18} />
        Durée par étape du pipeline — 30 jours
      </h2>
      <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
        Durée médiane des {data.recent_days} derniers jours comparée aux jours
        précédents. Les échecs sont comptés mais exclus des médianes : une étape
        qui plante vite n'est pas une étape rapide.
      </p>

      {!stages.length ? (
        <p className="mt-3 text-xs text-zinc-500" data-testid="pipeline-durations-empty">
          Aucune mesure encore enregistrée. Les durées se remplissent au fil des
          traitements, depuis la mise en place du chronomètre.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full text-left text-xs" data-testid="pipeline-durations-table">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="py-2 px-3 font-semibold">Étape</th>
                <th className="py-2 px-3 font-semibold text-right">
                  {data.recent_days} derniers jours
                </th>
                <th className="py-2 px-3 font-semibold text-right">Avant</th>
                <th className="py-2 px-3 font-semibold text-right">Variation</th>
                <th className="py-2 px-3 font-semibold text-right">Exécutions</th>
                <th className="py-2 px-3 font-semibold">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((s) => (
                <tr
                  key={s.stage}
                  className="border-t border-zinc-100"
                  data-testid={`pipeline-duration-row-${s.stage}`}
                >
                  <td className="py-2 px-3 text-zinc-800">{s.stage}</td>
                  <td className="py-2 px-3 text-right text-zinc-800">{secs(s.recent_p50_ms)}</td>
                  <td className="py-2 px-3 text-right text-zinc-600">{secs(s.before_p50_ms)}</td>
                  <td
                    className={`py-2 px-3 text-right font-medium ${
                      s.change_pct === null
                        ? "text-zinc-500"
                        : s.change_pct > 0
                        ? "text-red-700"
                        : "text-emerald-700"
                    }`}
                  >
                    {s.change_pct === null ? "—" : `${s.change_pct > 0 ? "+" : ""}${s.change_pct} %`}
                  </td>
                  <td className="py-2 px-3 text-right text-zinc-600">
                    {s.runs}
                    {s.failed ? (
                      <span className="text-red-700"> ({s.failed} échecs)</span>
                    ) : null}
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        VERDICT[s.verdict] || VERDICT.stable
                      }`}
                    >
                      {s.verdict}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default PipelineDurationsPanel;
